import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ApiError,
  ingestionApi,
  type IngestionJobDTO,
} from '@/shared/api'

interface Props {
  tenantId: string
  /** Any value that changes signals the panel to reload (last document_id, counter, etc.). */
  refreshToken?: string | number | null
  /**
   * Phase 3B — fired when a job the panel was actively watching transitions to
   * `indexed`. The parent reloads the catalog so the newly-indexed document
   * appears in the list and becomes usable as a chat scope, without an F5.
   */
  onIndexed?: () => void
}

/**
 * Phase 3B — while any job is queued/processing the panel re-polls on this
 * cadence so a queued upload updates to Indexed without a manual refresh. It
 * stops as soon as every job is terminal (indexed/failed) to avoid needless
 * background traffic.
 */
const POLL_INTERVAL_MS = 2500
const ACTIVE_STATUSES: ReadonlySet<IngestionJobDTO['status']> = new Set([
  'queued',
  'processing',
])

function isActiveStatus(status: IngestionJobDTO['status']): boolean {
  return ACTIVE_STATUSES.has(status)
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return iso
  }
}

function statusToTone(status: IngestionJobDTO['status']): string {
  if (status === 'indexed') return 'is-success'
  if (status === 'failed') return 'is-error'
  if (status === 'processing' || status === 'queued') return 'is-warning'
  return ''
}

/**
 * STEP 10.4 — recent ingestion jobs (read-only). Always shows the latest
 * entries for the current tenant so operators can spot failures quickly
 * without leaving the Documents page.
 *
 * Phase 3B — adds safe auto-polling: while a job is queued/processing (queued
 * ingestion mode) the panel re-fetches every few seconds and notifies the
 * parent the moment a watched job becomes `indexed`, so the document list and
 * chat scope refresh on their own. Polling pauses when nothing is active.
 */
export function IngestionJobsPanel({ tenantId, refreshToken, onIndexed }: Props) {
  const [jobs, setJobs] = useState<IngestionJobDTO[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Ids of jobs seen as queued/processing in the PREVIOUS fetch — used to fire
  // `onIndexed` exactly once when one of them flips to `indexed` (never on the
  // first load of already-indexed history).
  const prevActiveIdsRef = useRef<Set<string>>(new Set())
  // Keep the latest callback without making `load` depend on its identity.
  const onIndexedRef = useRef(onIndexed)
  useEffect(() => {
    onIndexedRef.current = onIndexed
  }, [onIndexed])

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true)
      setError(null)
      try {
        const response = await ingestionApi.listJobs(
          { tenantId, limit: 8 },
          signal,
        )
        const items = response.items
        const prevActive = prevActiveIdsRef.current
        const justIndexed = items.some(
          (j) => j.status === 'indexed' && prevActive.has(j.id),
        )
        prevActiveIdsRef.current = new Set(
          items.filter((j) => isActiveStatus(j.status)).map((j) => j.id),
        )
        setJobs(items)
        if (justIndexed) onIndexedRef.current?.()
      } catch (err) {
        // 503 (DATABASE_ENABLED=false) is acceptable in legacy local mode
        // — silently hide the panel instead of showing a scary error.
        if (err instanceof ApiError && err.status === 503) {
          setJobs([])
          setError(null)
          return
        }
        if (err instanceof DOMException && err.name === 'AbortError') {
          return
        }
        setError(errorDetail(err, 'Falha ao listar ingestões.'))
      } finally {
        setLoading(false)
      }
    },
    [tenantId],
  )

  // Initial load + reload whenever the tenant or the upload refresh token changes.
  useEffect(() => {
    if (!tenantId) return
    const ctrl = new AbortController()
    void load(ctrl.signal)
    return () => ctrl.abort()
  }, [tenantId, load, refreshToken])

  const hasActiveJobs = useMemo(
    () => jobs.some((j) => isActiveStatus(j.status)),
    [jobs],
  )

  // Auto-poll only while at least one job is queued/processing. The effect's
  // cleanup clears the timer and aborts the in-flight poll, so it naturally
  // stops the moment everything is indexed/failed (hasActiveJobs → false).
  useEffect(() => {
    if (!tenantId || !hasActiveJobs) return
    const ctrl = new AbortController()
    const timerId = window.setInterval(() => {
      void load(ctrl.signal)
    }, POLL_INTERVAL_MS)
    return () => {
      ctrl.abort()
      window.clearInterval(timerId)
    }
  }, [tenantId, hasActiveJobs, load])

  if (!tenantId) return null
  if (!error && jobs.length === 0 && !loading) return null

  return (
    <section className="doc-jobs-panel" aria-labelledby="doc-jobs-title">
      <header className="doc-jobs-head">
        <h3 id="doc-jobs-title">Ingestões recentes</h3>
        <button
          type="button"
          className="settings-button"
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? 'Atualizando…' : 'Atualizar'}
        </button>
      </header>
      {hasActiveJobs ? (
        <p className="doc-jobs-live" role="status" aria-live="polite">
          <span className="doc-jobs-live-dot" aria-hidden="true" />
          Processando em segundo plano · atualizando automaticamente…
        </p>
      ) : null}
      {error ? (
        <div className="settings-alert is-error" role="alert">
          {error}
        </div>
      ) : null}
      {jobs.length === 0 && !error ? (
        <p className="settings-empty">Sem ingestões registradas ainda.</p>
      ) : (
        <ul className="doc-jobs-list">
          {jobs.map((job) => (
            <li key={job.id} className={`doc-jobs-item ${statusToTone(job.status)}`}>
              <div className="doc-jobs-row">
                <strong>{job.filename ?? job.document_external_id ?? '—'}</strong>
                <span className="doc-jobs-status">{job.status}</span>
              </div>
              <div className="doc-jobs-meta">
                {job.document_kind ?? '—'} · iniciado {fmtTime(job.started_at)}
                {job.finished_at ? ` · finalizado ${fmtTime(job.finished_at)}` : ''}
              </div>
              {job.error_message ? (
                <div className="doc-jobs-error">{job.error_message}</div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
